/**
 * Domain types for study feature (科目一覧).
 */
import { valueOf } from "@/types/common";
import { TVocabularyAnswerResult } from "@/infrastructure/adapters/vocabulary/vocabulary.types";

/** A single study subject */
export type TStudySubject = {
  id: string;
  /** Subject name (e.g., 建設基礎知識) */
  name: string;
  /** Whether the learner has completed this subject */
  isCompleted: boolean;
  /** Whether the learner has level assessment this subject at least once */
  hasLevelAssessment: boolean;
  /** Whether the learner has answered the diagnosis quiz */
  hasAnswered: boolean;
  /** Whether subject is locked for trial learners */
  isLocked: boolean;
};

/** Level diagnosis info for first-time study */
export type TLevelDiagnosis = {
  /** Total number of questions */
  totalQuestionCount: number;
  /** Estimated time in minutes */
  estimatedMinutes: number;
};

/** A single option in a diagnosis quiz question */
export type TDiagnosisQuestionOption = {
  text: string;
  translatedText?: string | null;
};

/** A single diagnosis quiz question */
export type TDiagnosisQuestion = {
  /** Question text (e.g., 「基礎」の意味は次のうちどれか？) */
  questionText: string;
  /** Translated question text (non-Japanese locale) */
  translatedQuestionText?: string | null;
  /** Optional image S3 path displayed below question text (resolved to a signed URL at render time) */
  imageFile?: string;
  /** Answer options (variable count, 2-4) */
  options: TDiagnosisQuestionOption[];
  /** Index of the correct answer — undefined when API doesn't provide it */
  correctIndex?: number;
  /** Question type: vocabulary or practice */
  type?: "vocabulary" | "practice";
  /** Difficulty level (1-5) */
  level: number;
  /** Explanation shown in result screen */
  explanation: string;
  /** Translated explanation (non-Japanese locale) */
  translatedExplanation?: string | null;
  /** BE session item ID for answer submission */
  sessionItemId?: string;
  /** BE question ID */
  questionId?: number;
};

/** Diagnosis quiz session */
export type TDiagnosisQuizSession = {
  /** BE session ID for answer submission */
  sessionId?: string;
  questions: TDiagnosisQuestion[];
  totalCount: number;
};

export type TDiagnosisQuizMetadata = {
  totalQuestions: number;
};

/** Submit answer request */
export type TDiagnosisAnswerRequest = {
  sessionItemId: number;
  selectedOptionIndex: number;
  /** When true, force-completes the session — remaining questions marked as wrong */
  forceComplete?: boolean;
};

/** Submit answer response */
export type TDiagnosisAnswerResult = {
  sessionItemId: number;
  correctIndex: number;
  /** Returned when forceComplete — correct answers for remaining questions */
  unansweredItems?: { sessionItemId: number; correctIndex: number }[];
};

/** A practice level within a subject (Lv1-Lv5) */
export type TSubjectPracticeLevel = {
  /** Level number (1-5) */
  level: number;
  /** Number of questions answered correctly at least once */
  practiceCorrectCount: number;
  /** Total number of questions */
  questionCount: number;
  /** Whether confirmation test has been completed */
  testCompleted: boolean;
};

/** A single word practice item (flashcard) */
export type TWordPracticeItem = {
  /** Published vocabulary ID (for progress tracking) */
  id: number;
  /** Kanji form (e.g., 基礎) */
  kanji: string;
  /** Furigana reading (e.g., きそ) */
  furigana: string;
  /** Translation in learner's language (e.g., Foundation) */
  translation: string;
  /** Image file S3 path — resolved to a signed URL at render time (absent in mock data) */
  imageFile?: string | null;
  /** Signed URL pre-resolved by BE; used to prime the signed-url cache (skips the per-word API call) */
  imageUrl?: string | null;
};

/** Word practice session data */
export type TWordPracticeSession = {
  words: TWordPracticeItem[];
  totalCount: number;
};

/** A single word quiz question (4-choice from translation) */
export type TWordQuizQuestion = {
  /** The word being tested */
  word: TWordPracticeItem;
  /** 4 translation options (1 correct + 3 distractors) */
  options: string[];
  /** Index of the correct answer in options array */
  correctIndex: number;
};

/** Word quiz session data */
export type TWordQuizSession = {
  questions: TWordQuizQuestion[];
  totalCount: number;
};

/** Level practice session data (Lv1-Lv5 練習) — reuses TDiagnosisQuestion structure */
export type TLevelPracticeSession = {
  /** Level number (1-5) */
  level: number;
  /** Level name (e.g., 言葉の意味) */
  levelName: string;
  questions: TDiagnosisQuestion[];
  totalCount: number;
};

/** Text explanation session data (テキスト解説を閲覧) */
export type TTextExplanationSession = {
  subjectName: string;
  pages: string[];
  totalPages: number;
};

/** Recommendation type from BE API */
/** おすすめアクション種別 */
export const RECOMMENDATION_TYPE = {
  ASSESSMENT: "ASSESSMENT",
  VOCAB_PRACTICE: "VOCAB_PRACTICE",
  VOCAB_QUIZ: "VOCAB_QUIZ",
  LEVEL_PRACTICE: "LEVEL_PRACTICE",
  LEVEL_QUIZ: "LEVEL_QUIZ",
  MOCK_EXAM: "MOCK_EXAM",
} as const;
export type TRecommendationType = valueOf<typeof RECOMMENDATION_TYPE>;

/** Recommendation from GET /learning/recommendations */
export type TRecommendation = {
  /** Source subject ID for navigation */
  publishedSubjectId: number;
  /** Subject name (with partType if applicable) */
  subjectName: string;
  /** Recommendation action type */
  type: TRecommendationType;
  /** Next level the learner should tackle (lv1-lv5). Server already computes this from current_level + assessment cascade. null for assessment/MOCK_EXAM. */
  level: string | null;
  /** Display text (e.g., "次：レベル診断") */
  displayText: string;
};

/** Study page data */
export type TStudyPageData = {
  recommendation: TRecommendation | null;
  subjects: TStudySubject[];
  /** Whether the learner is on a trial contract */
  isTrial: boolean;
};

/* ── Subject Quiz Session Types (BE API) ── */

/** Level item returned by GET /learning/subjects/{id}/levels */
export type TSubjectLevelDTO = {
  level: string;
  name: string;
  questionCount: number;
  practiceCorrectCount: number;
  isPracticeCompleted: boolean;
  isQuizCompleted: boolean;
};

/** Response wrapper for GET /learning/subjects/{id}/levels */
export type TSubjectLevelsResponse = {
  levels: TSubjectLevelDTO[];
  vocabularyCount: number;
  learnedVocabularyCount: number;
  textbookFileUrl: string;
  isVocabularyQuizCompleted: boolean;
  /** Highest level passed via quiz (≥80%); defaults to 'lv0' when no record exists. */
  currentLevel: string;
};

/** A single choice within a quiz question */
export type TSubjectQuizChoice = {
  choiceId: string;
  text: string;
};

/** A quiz question within a session */
export type TSubjectQuizQuestion = {
  sessionItemId: string;
  questionId: number;
  questionText: string;
  imageFile: string | null;
  choices: TSubjectQuizChoice[];
  /** Results of the last 3 attempts on this question, oldest first */
  recentResults: TVocabularyAnswerResult[];
  /** Cumulative correct-answer count, drives the ✓ mastered badge */
  masteryCorrectCount: number;
};

/** Raw DTO from POST /learning/subjects/{id}/quiz/sessions (choices[] instead of options[]) */
export type TSubjectQuizSessionDTO = {
  quizSessionId: string;
  totalQuestions: number;
  questions: Array<{
    sessionItemId: string;
    questionId: number;
    questionText: string;
    imageFile: string | null;
    choices: Array<{ choiceId: string; text: string }>;
    /** Flags per attempt — "1" correct, "0" wrong, leftmost oldest */
    recentResults?: string | null;
    masteryCorrectCount?: number | null;
  }>;
};

/** Mapped domain model for a quiz session */
export type TSubjectQuizSession = {
  quizSessionId: string;
  totalQuestions: number;
  questions: TSubjectQuizQuestion[];
};

/** Request body for POST /learning/subject-quiz/sessions/{id}/answers */
export type TSubjectQuizAnswerRequest = {
  sessionItemId: string;
  choiceId: string;
};

/** Response from submit answer API */
export type TSubjectQuizAnswerResponse = {
  sessionItemId: string;
  isCorrect: boolean;
  correctCount: number;
  wrongCount: number;
  answeredCount: number;
  totalQuestions: number;
  isCompleted: boolean;
};

/** A single option in quiz result item */
export type TSubjectQuizResultOption = {
  optionId: string;
  text: string;
  /** Translated option text (non-Japanese locale) */
  translatedText: string | null;
};

/** A single item in quiz result */
export type TSubjectQuizResultItem = {
  questionId: number;
  displayOrder: number;
  questionText: string;
  /** Translated question text (non-Japanese locale) */
  translatedQuestionText: string | null;
  imageFile: string | null;
  isCorrect: boolean;
  selectedOptionId: string | null;
  correctOptionId: string;
  selectedOptionText: string | null;
  correctOptionText: string;
  /** Translated selected option text (non-Japanese locale) */
  selectedOptionTranslatedText: string | null;
  /** Translated correct option text (non-Japanese locale) */
  correctOptionTranslatedText: string | null;
  explanation: string | null;
  /** Translated explanation text (non-Japanese locale) */
  translatedExplanation: string | null;
  options: TSubjectQuizResultOption[];
};

/** Full quiz result from GET /learning/subject-quiz/sessions/{id}/result */
export type TSubjectQuizResult = {
  quizSessionId: string;
  correctCount: number;
  wrongCount: number;
  answeredCount: number;
  totalQuestions: number;
  accuracyRate: number;
  isCompleted: boolean;
  items: TSubjectQuizResultItem[];
};

/* ── Study Vocabulary Quiz Session Types (BE API) ── */

/** A single choice in study vocabulary quiz question */
export type TStudyVocabQuizChoice = {
  choiceId: string;
  text: string;
};

/** A study vocabulary quiz question within a session */
export type TStudyVocabQuizQuestion = {
  sessionItemId: string;
  vocabularyId: number;
  word: string;
  furigana: string | null;
  choices: TStudyVocabQuizChoice[];
  /** Results of the last 3 attempts on this word, oldest first */
  recentResults: TVocabularyAnswerResult[];
  /** Cumulative correct-answer count, drives the ✓ mastered badge */
  masteryCorrectCount: number;
};

/** Study vocabulary quiz session from POST /learning/subjects/{id}/vocabulary-quiz/sessions */
export type TStudyVocabQuizSession = {
  quizSessionId: string;
  totalQuestions: number;
  questions: TStudyVocabQuizQuestion[];
};

/** Raw DTO for the session above — mastery fields arrive as BE flags */
export type TStudyVocabQuizSessionDTO = {
  quizSessionId: string;
  totalQuestions: number;
  questions: Array<
    Omit<TStudyVocabQuizQuestion, "recentResults" | "masteryCorrectCount"> & {
      /** Flags per attempt — "1" correct, "0" wrong, leftmost oldest */
      recentResults?: string | null;
      masteryCorrectCount?: number | null;
    }
  >;
};

/** Request body for POST /learning/subject-vocabulary-quiz/sessions/{id}/answers */
export type TStudyVocabQuizAnswerRequest = {
  sessionItemId: string;
  choiceId: string;
};

/** Response from study vocabulary quiz submit answer API */
export type TStudyVocabQuizAnswerResponse = {
  sessionItemId: string;
  isCorrect: boolean;
  correctCount: number;
  wrongCount: number;
  answeredCount: number;
  totalQuestions: number;
  isCompleted: boolean;
};

/** A single item in study vocabulary quiz result */
export type TStudyVocabQuizResultItem = {
  questionId: number;
  displayOrder: number;
  word: string;
  furigana: string | null;
  isCorrect: boolean;
  selectedOptionId: string | null;
  correctOptionId: string;
  selectedOptionText: string | null;
  correctOptionText: string;
  options: TStudyVocabQuizResultOption[];
};

/** Option in study vocabulary quiz result item */
export type TStudyVocabQuizResultOption = {
  optionId: string;
  text: string;
};

/** Full study vocabulary quiz result from GET /learning/subject-vocabulary-quiz/sessions/{id}/result */
export type TStudyVocabQuizResult = {
  quizSessionId: string;
  correctCount: number;
  wrongCount: number;
  answeredCount: number;
  totalQuestions: number;
  accuracyRate: number;
  isCompleted: boolean;
  items: TStudyVocabQuizResultItem[];
};

/* ── Subject Quiz Practice Session Types (BE API) ── */

/** A single choice within a quiz practice question */
export type TSubjectQuizPracticeChoice = {
  choiceId: string;
  text: string;
  /** Translated choice text (non-Japanese locale) */
  translatedText: string | null;
};

/** A quiz practice question (delivered one at a time) */
export type TSubjectQuizPracticeQuestion = {
  sessionItemId: string;
  questionId: number;
  questionText: string;
  /** Translated question text (non-Japanese locale) */
  translatedQuestionText: string | null;
  imageFile: string | null;
  choices: TSubjectQuizPracticeChoice[];
};

/** Session from POST /learning/subjects/{id}/quiz-practice/sessions */
export type TSubjectQuizPracticeSession = {
  quizSessionId: string;
  totalQuestions: number;
  questions: TSubjectQuizPracticeQuestion[];
};

/** Request body for POST /learning/subject-quiz-practice/sessions/{id}/answers */
export type TSubjectQuizPracticeAnswerRequest = {
  sessionItemId: string;
  choiceId: string;
};

/** Response from submit quiz practice answer API */
export type TSubjectQuizPracticeAnswerResponse = {
  sessionItemId: string;
  isCorrect: boolean;
  correctChoiceId: string;
  explanation: string | null;
  /** Translated explanation text (non-Japanese locale) */
  translatedExplanation: string | null;
  correctCount: number;
  wrongCount: number;
  answeredCount: number;
  totalQuestions: number;
  isCompleted: boolean;
  nextQuestion: TSubjectQuizPracticeQuestion | null;
};

/** Practice result reuses quiz result item/option shapes — same BE contract */
export type TSubjectQuizPracticeResultOption = TSubjectQuizResultOption;
export type TSubjectQuizPracticeResultItem = TSubjectQuizResultItem;

/** Full practice result from GET /learning/subject-quiz-practice/sessions/{id}/result */
export type TSubjectQuizPracticeResult = TSubjectQuizResult;
